import * as React from 'react';
import Text from 'antd/lib/typography/Text';
import Carousel from 'antd/lib/carousel';
import { isNil } from 'lodash';
import { IntegrationInstallationId, IntegrationType, ReleaseAdoption, SubscriptionFeatureType } from '@/types';
import { antGrey, runwayColors } from '../styles/globalStyles';
import { PlanRestrictedFeatureWrapper } from '../planRestrictions/PlanRestrictedFeatureWrapper';
import IntegrationIcon from '../icons/IntegrationIcon';
import MultiIntegrationIcon from '../icons/MultiIntegrationIcon';
import { AdoptionBigNumber, AdoptionVersionBars } from './AdoptionRateChart';

export interface AdoptionCarouselEntry {
    integrationInstallationId: IntegrationInstallationId;
    sessionAdoption?: number;
    adoptionRates: ReleaseAdoption[];
    integrationId?: IntegrationType;
}

interface AdoptionRateCarouselProps {
    title: string;
    entries: AdoptionCarouselEntry[];
    isMultiApk: boolean;
    currentReleaseVersion?: string;
    shouldShowDisabledUI?: boolean;
}

// Averages adoption rate per release across all providers. Each provider measures
// its own user population, so this is a rough blended view shown on the overview
// page only — the per-provider pages keep each integration's own numbers.
const averageAdoptionRatesAcrossProviders = (entries: AdoptionCarouselEntry[]): ReleaseAdoption[] => {
    // Keyed by releaseId; Map insertion order preserves the version ordering.
    const byReleaseId = new Map<string, { sum: number; count: number; sample: ReleaseAdoption }>();
    for (const entry of entries) {
        for (const adoptionRate of entry.adoptionRates ?? []) {
            let agg = byReleaseId.get(adoptionRate.releaseId);
            if (!agg) {
                agg = { sum: 0, count: 0, sample: adoptionRate };
                byReleaseId.set(adoptionRate.releaseId, agg);
            }
            if (!isNil(adoptionRate.adoptionRate)) {
                agg.sum += adoptionRate.adoptionRate;
                agg.count += 1;
            }
        }
    }
    return Array.from(byReleaseId.values()).map((agg) => ({
        ...agg.sample,
        adoptionRate: agg.count > 0 ? agg.sum / agg.count : undefined,
    }));
};

const CardChrome: React.FC<React.PropsWithChildren> = ({ children }) => (
    <div
        style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            borderRadius: '8px',
            color: antGrey.grey7,
            // Trimmed bottom padding so the carousel dots sit near the card border.
            padding: '18px 18px 10px 18px',
            border: `1px solid ${antGrey.grey5}`,
            minWidth: 232,
            flexGrow: 1,
            height: '100%',
        }}
    >
        {children}
    </div>
);

// A provider page passes its integrationId and shows that logo; the cover page
// passes none and shows the generic "multiple integrations" icon.
const CardTitle: React.FC<{ title: string; integrationId?: IntegrationType }> = ({ title, integrationId }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <PlanRestrictedFeatureWrapper feature={SubscriptionFeatureType.Rollouts} requiresTooltip>
            <Text style={{ color: antGrey.grey7 }}>{title}</Text>
        </PlanRestrictedFeatureWrapper>
        <div>
            {integrationId ?
                <IntegrationIcon integrationId={integrationId} />
            :   <MultiIntegrationIcon size={20} color={runwayColors.black} />}
        </div>
    </div>
);

// Page 1: the blended overview — a generic top-right icon, one big number per
// provider (each with its own logo) spread across the bar width, and a single
// averaged set of per-version bars.
const OverviewPage: React.FC<AdoptionRateCarouselProps> = ({
    title,
    entries,
    isMultiApk,
    currentReleaseVersion,
    shouldShowDisabledUI,
}) => {
    const averagedAdoptionRates = React.useMemo(() => averageAdoptionRatesAcrossProviders(entries), [entries]);

    // Shrink the numbers as providers are added so they stay on one line, evenly
    // spread across the full (bar) width.
    const bigNumberFontSize = entries.length >= 3 ? 22 : 30;

    return (
        <>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                <CardTitle title={title} />
                <div
                    style={{
                        display: 'flex',
                        flexWrap: 'nowrap',
                        gap: 8,
                        marginTop: 4,
                        width: '100%',
                        justifyContent: 'space-between',
                    }}
                >
                    {entries.map((entry) => (
                        <AdoptionBigNumber
                            key={entry.integrationInstallationId}
                            sessionAdoption={entry.sessionAdoption}
                            integrationId={entry.integrationId}
                            fontSize={bigNumberFontSize}
                        />
                    ))}
                </div>
            </div>
            {!isMultiApk && averagedAdoptionRates.length > 0 && (
                <AdoptionVersionBars
                    adoptionRates={averagedAdoptionRates}
                    isMultiAPK={isMultiApk}
                    currentReleaseVersion={currentReleaseVersion}
                    shouldShowDisabledUI={shouldShowDisabledUI}
                />
            )}
        </>
    );
};

// Pages 2...N: one provider's adoption, identical in content to the standalone
// AdoptionRateChart (logo top-right, big number, per-version bars).
const ProviderPage: React.FC<{
    title: string;
    entry: AdoptionCarouselEntry;
    isMultiApk: boolean;
    currentReleaseVersion?: string;
    shouldShowDisabledUI?: boolean;
}> = ({ title, entry, isMultiApk, currentReleaseVersion, shouldShowDisabledUI }) => {
    const showPreviousReleases = !isMultiApk;
    return (
        <>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                <CardTitle title={title} integrationId={entry.integrationId} />
                <AdoptionBigNumber sessionAdoption={entry.sessionAdoption} />
            </div>
            {showPreviousReleases && entry.adoptionRates?.length > 0 && (
                <AdoptionVersionBars
                    adoptionRates={entry.adoptionRates}
                    isMultiAPK={isMultiApk}
                    currentReleaseVersion={currentReleaseVersion}
                    shouldShowDisabledUI={shouldShowDisabledUI}
                />
            )}
        </>
    );
};

// Slides fill the card height (the slick chain is forced to 100% height in
// index.less) and distribute content top→bottom, so the card uses its full
// vertical space. A minHeight keeps a sensible size when the card isn't
// stretched by a taller neighbour in the grid row. The bottom padding reserves
// clear space for the dots, which slick positions absolutely near the bottom.
const slideContentStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    gap: 12,
    height: '100%',
    minHeight: 168,
    paddingBottom: 18,
};

const AdoptionRateCarousel: React.FC<AdoptionRateCarouselProps> = (props) => {
    const { title, entries, isMultiApk, currentReleaseVersion, shouldShowDisabledUI } = props;

    // A flat, keyed slide array avoids any ambiguity in how react-slick counts
    // slides (page 0 = blended overview, pages 1..N = one per provider).
    const slides = [
        <div key="overview" style={slideContentStyle}>
            <OverviewPage {...props} />
        </div>,
        ...entries.map((entry) => (
            <div key={entry.integrationInstallationId} style={slideContentStyle}>
                <ProviderPage
                    title={title}
                    entry={entry}
                    isMultiApk={isMultiApk}
                    currentReleaseVersion={currentReleaseVersion}
                    shouldShowDisabledUI={shouldShowDisabledUI}
                />
            </div>
        )),
    ];

    return (
        <PlanRestrictedFeatureWrapper feature={SubscriptionFeatureType.Rollouts}>
            {(isDisabled) =>
                isDisabled || shouldShowDisabledUI ?
                    <CardChrome>
                        <CardTitle title={title} />
                        <div
                            style={{
                                backgroundColor: antGrey.grey2,
                                padding: 8,
                                marginTop: 16,
                                borderRadius: 4,
                                width: '100%',
                            }}
                        >
                            <Text style={{ color: antGrey.grey7, fontSize: 12 }}>Not available on Free plan.</Text>
                        </div>
                    </CardChrome>
                :   <CardChrome>
                        <Carousel
                            rootClassName="adoption-rate-carousel-root"
                            className="adoption-rate-carousel"
                            dots
                            dotPosition="bottom"
                            arrows={false}
                            infinite={false}
                            draggable
                        >
                            {slides}
                        </Carousel>
                    </CardChrome>
            }
        </PlanRestrictedFeatureWrapper>
    );
};

export default AdoptionRateCarousel;
